import type { JSX } from 'react';
import { NavLink } from 'react-router-dom';
import { Chip } from '@heroui/react';

import type { PatternManifestEntry } from '~/types';

import { formatPatternTitle } from '../utils';

import { SeverityChip } from './SeverityChip';

interface PatternCardProps {
  pattern: PatternManifestEntry;
}

const MAX_VISIBLE_TAGS = 4;

export function PatternCard({ pattern }: PatternCardProps): JSX.Element {
  const tags = pattern.tags ?? [];
  const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
  const overflowCount = tags.length - MAX_VISIBLE_TAGS;

  return (
    <NavLink
      to={`/patterns/${pattern.pattern_id}`}
      className="block p-4 rounded-lg border border-default bg-surface hover:bg-surface-elevated transition-colors"
    >
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-sm font-semibold text-foreground truncate">
          {formatPatternTitle(pattern.pattern_id)}
        </h3>
        <SeverityChip severity={pattern.severity} />
        {pattern.type === 'anti-pattern' && (
          <Chip size="sm" color="warning" variant="flat">
            Anti-Pattern
          </Chip>
        )}
      </div>
      <p className="text-xs text-muted line-clamp-2 mb-3">{pattern.intent}</p>
      {visibleTags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {visibleTags.map(tag => (
            <Chip key={tag} size="sm" variant="bordered" className="text-xs">
              {tag}
            </Chip>
          ))}
          {overflowCount > 0 && (
            <span className="text-xs text-muted self-center">+{overflowCount} more</span>
          )}
        </div>
      )}
    </NavLink>
  );
}
